import React from 'react'

import { useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import Flex from 'styled-flex-component'

import { IModalComponentProps } from '@context/Modal'
import { useIsMounted } from '@hooks/useIsMounted'
import { IApplicationState } from '@store'
import {
  clearBrandFansErrors,
  IBrandFanEditableFields,
  selectBrandFanGroupDescription,
  selectBrandFanGroupName,
  selectIsUpdatingBrandFanGroup,
  updateBrandFanGroup,
} from '@store/brandFans'
import Theme from '@theme'
import { Button, Container, H1, Header, Modal, TextArea } from '@tribegroup'
import BrandFansGroupModalErrorMessage from './ErrorMessage'

interface IProps extends IModalComponentProps {
  groupId: number
}

const BrandFansGroupModalEditDescription: React.FC<IProps> = ({
  groupId,
  isOpen = false,
  onRequestClose,
}) => {
  const dispatch = useDispatch()
  const description = useSelector((state: IApplicationState) =>
    selectBrandFanGroupDescription(state, groupId),
  )
  const name = useSelector((state: IApplicationState) => selectBrandFanGroupName(state, groupId))
  const isUpdatingBrandFanGroup = useSelector((state: IApplicationState) =>
    selectIsUpdatingBrandFanGroup(state),
  )

  const {
    register,
    handleSubmit,
    getValues,
    trigger,
    formState: { isDirty, errors },
  } = useForm()

  // react-hook-form v7 returns { name, onChange, onBlur, ref } from register().
  // tribegroup components forward their DOM ref via `innerRef`.
  const { ref: descriptionRef, ...descriptionField } = register('description', { maxLength: 120 })

  const onSubmit = (params: IBrandFanEditableFields) => {
    dispatch(updateBrandFanGroup(groupId, params))
  }
  const onClick = async () => {
    if (await trigger()) {
      dispatch(updateBrandFanGroup(groupId, { name, description: getValues().description.trim() }))
    }
  }
  const isMounted = useIsMounted()
  React.useEffect(() => {
    if (isMounted && !isUpdatingBrandFanGroup) {
      onRequestClose()
    }
  }, [isUpdatingBrandFanGroup])
  React.useEffect(() => {
    return () => {
      dispatch(clearBrandFansErrors())
    }
  })

  return (
    <Modal reactModalProps={{ isOpen }} width={33.75} complex>
      <form
        data-testid="brand-fans-group-modal-edit-description-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <H1 color={Theme.grey900Color} bottomOuterSpacing={1.75} capitalize>
          <FormattedMessage id="brandFans.groups.actions.editDescription" />
        </H1>
        <Container bottomOuterSpacing={4.375} relative>
          <TextArea
            {...descriptionField}
            innerRef={descriptionRef}
            defaultValue={description || ''}
            height={4.25}
            characterLimit={120}
            autoFocus
            data-testid="brand-fans-group-modal-description"
            disableLineBreak
          />
          {errors.description && (
            <BrandFansGroupModalErrorMessage bottomPosition={0}>
              <FormattedMessage id="builder.campaign.error.max" />
            </BrandFansGroupModalErrorMessage>
          )}
        </Container>
      </form>
      <Flex justifyEnd alignCenter>
        <Button
          width={8.75}
          scheme="primary"
          height={3}
          outlined
          onClick={onRequestClose}
          disabled={isUpdatingBrandFanGroup}
        >
          <Header cta>
            <FormattedMessage id="core.text.cancel" />
          </Header>
        </Button>
        <Button
          width={8.75}
          scheme="primary"
          height={3}
          leftOuterSpacing={0.75}
          onClick={onClick}
          data-testid="brand-fans-group-update"
          disabled={isUpdatingBrandFanGroup || !isDirty}
          loading={isUpdatingBrandFanGroup}
        >
          <Header cta>
            <FormattedMessage id="core.text.update" />
          </Header>
        </Button>
      </Flex>
    </Modal>
  )
}

export { BrandFansGroupModalEditDescription }
export default BrandFansGroupModalEditDescription
